import React, { CSSProperties, FC, useState, useMemo } from "react";
import "./fraudGuard.scss";
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import VerticalCaret from "./ui/verticalCaret";
import { DecisionRule } from "../models/applications/applicationInterfaces";
import JsonDisplay from "./ui/JsonDisplay";
import { flattenToDotNotation } from "../utils/formatting";
import { selectOrg } from "../models/organization/organizationSlice";
import { useSelector } from "react-redux";
const EXPECTED = "EXPECTED";
const RETURNED = "RETURNED";
const APPROVED = "approved";
const DENIED = "denied";
const REVIEW = "review";

const GRAY = "#dfdfdf";
const WHITE = "#ffffff";
const TEAL = "#00b4a1";
const ORANGE = "#FA8C16";
const RED = "#eb2835";
const LIGHTGRAY = "#f0f0f0";

export interface DecisionIntelligenceItemProps {
  rule: DecisionRule;
  allowApprovalColor?: boolean;
}

export const DecisionIntelligenceItem: FC<DecisionIntelligenceItemProps> = ({
  rule,
  allowApprovalColor,
}) => {
  const { title, matched, decisionStatus, data, conditions } = rule;
  const { decision } = decisionStatus || {};
  const [showDetails, setShowDetails] = useState(false);
  const [tab, setTab] = useState(EXPECTED);
  const organization = useSelector(selectOrg);
  const { hideDecisionIntelligenceDetails } = organization.meta || {};

  // Displays caret for toggling decision details unless explicitly disabled

  const expectedData = conditions || "No data is available for this rule.";
  const returnedData = data
    ? flattenToDotNotation(data)
    : "No data is available for this rule.";

  const getDecisionColor = (decision: string | undefined) => {
    switch (decision) {
      case APPROVED:
        return allowApprovalColor ? TEAL : "inherit";
      case REVIEW:
        return ORANGE;
      case DENIED:
        return RED;
    }
  };

  const activeColor = useMemo(
    () => (matched ? getDecisionColor(decision) : undefined),
    [decisionStatus]
  );

  // This is the status text that appears to the right of the rule
  const statusText = useMemo(() => {
    // If the rule is matched (triggered), use the decision status title
    if (matched) {
      return decisionStatus?.title || decision;
    }
    // For rules that would trigger an approved decision, show "Skipped" if they are not matched
    if (!matched && decision === APPROVED) {
      return "Skipped";
    }
    // For unmatched (non-triggered) rules, show "Completed"
    return "Completed";
  }, [decisionStatus]);

  // RENDER ---------------------------------
  return (
    <div
      className="br-2 mt-2"
      style={{
        backgroundColor: WHITE,
        border: `1px solid ${GRAY}`,
        padding: "12px",
      }}
    >
      <div
        className={`d-flex justify-content-between align-items-center ${
          !hideDecisionIntelligenceDetails && "pointer"
        }`}
        onClick={() =>
          !hideDecisionIntelligenceDetails && setShowDetails(!showDetails)
        }
      >
        <div className="d-flex align-items-center w-70">
          <DecisionIcon matched={matched} decision={decision} />
          <div className="f-3 font-weight-bold">{title}</div>
        </div>
        <div className="d-flex align-items-center w-30 justify-content-end text-right">
          <div
            className="font-weight-bold f-3 mr-2"
            style={{ color: activeColor }}
          >
            {statusText}
          </div>
          {/* Caret for toggling details - Can be hidden in org meta) */}
          {!hideDecisionIntelligenceDetails && (
            <VerticalCaret collapsed={showDetails} />
          )}
        </div>
      </div>
      {/* When Expanded - Rule Details */}
      {showDetails && (
        <>
          <div
            className="d-flex fwt f-3 my-3"
            style={{ borderBottom: `1px solid ${LIGHTGRAY}` }}
          >
            <div
              className="mr-3 pb-1 pointer"
              style={{
                borderBottom: `2px solid ${
                  tab === EXPECTED ? `black` : `transparent`
                }`,
              }}
              onClick={() => setTab(EXPECTED)}
            >
              Expected
            </div>
            <div
              className="pb-1 pointer"
              style={{
                borderBottom: `2px solid ${
                  tab === RETURNED ? `black` : `transparent`
                }`,
              }}
              onClick={() => setTab(RETURNED)}
            >
              Returned
            </div>
          </div>
          <JsonDisplay
            key={tab} // Update the key to force a re-render
            json={tab === EXPECTED ? expectedData : returnedData}
          />
        </>
      )}
    </div>
  );
};

// DecisionIcon SubComponent
export const DecisionIcon: FC<{ matched?: boolean; decision?: string }> = ({
  matched,
  decision,
}) => {
  const classes = "mr-2 f-5";
  if (!matched) {
    switch (decision) {
      case "approved":
        return (
          <MinusCircleOutlined className={classes} style={{ color: GRAY }} />
        );
      default:
        return (
          <CheckCircleOutlined className={classes} style={{ color: TEAL }} />
        );
    }
  }
  switch (decision) {
    case "review":
      return (
        <InfoCircleOutlined className={classes} style={{ color: ORANGE }} />
      );
    case "denied":
      return (
        <ExclamationCircleOutlined className={classes} style={{ color: RED }} />
      );
    default:
      return (
        <CheckCircleOutlined className="mr-2 f-5" style={{ color: TEAL }} />
      );
  }
};
